import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import { select, Store } from "@ngrx/store";
import {
	COMMON_ROUTE,
	TermsAndConditionsAction,
	TermsAndConditionsConfigService,
	TermsAndConditionsSelector,
	TermsAndConditionsVersion,
} from "@obg/ngx.common";
import {
	AuthSelector,
	CoreState,
	CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT,
	CustomerManagementSelector,
	Jurisdiction,
	JurisdictionSelector,
	Message,
	MessageService,
	MessageType,
} from "@obg/ngx.core";
import { WindowRef } from "@obg/ngx.global.evo";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { merge, Observable, Subscription } from "rxjs";
import { filter, take, tap } from "rxjs/operators";

import { TERMS_AND_CONDITIONS_ROUTES } from "./sm-terms-and-conditions.const";
import { UpdatedTermsAndConditionsService } from "./updated-terms-and-conditions/updated-terms-and-conditions.service";

@Component({
	selector: "obg-m-sm-terms-and-conditions-container",
	templateUrl: "./sm-terms-and-conditions.container.html",
	styleUrls: ["./sm-terms-and-conditions.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class SmTermsAndConditionsContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-sm-terms-and-conditions") class = true;

	jurisdiction: Jurisdiction | undefined;
	errorMessage: string | undefined;
	declineFormGroup = this.updatedTermsAndConditionsService.declineFormGroup;
	isAtDeclineWarningState$ = this.updatedTermsAndConditionsService.getIsDeclineWarningState$;
	versionHistory: TermsAndConditionsVersion[] | undefined;
	currentVersion: TermsAndConditionsVersion | undefined;
	termsAndConditionsHistoryEnabled = this.termsAndConditionsConfigService.isHistoryEnabled();
	latestVersion$ = this.store.select(this.termsAndConditionsSelector.getLatestVersion());
	isAuthenticated$ = this.store.select(AuthSelector.isAuthenticated);

	selectedIndex = 0;

	private readonly TERMS_AND_CONDITIONS_ROUTES = [TERMS_AND_CONDITIONS_ROUTES[0], TERMS_AND_CONDITIONS_ROUTES[1]];

	private data$$: Subscription | undefined;

	constructor(
		private store: Store<CoreState>,
		private updatedTermsAndConditionsService: UpdatedTermsAndConditionsService,
		private messageService: MessageService,
		private changeDetector: ChangeDetectorRef,
		private windowRef: WindowRef,
		private termsAndConditionsConfigService: TermsAndConditionsConfigService,
		private termsAndConditionsSelector: TermsAndConditionsSelector,
		private termsAndConditionsAction: TermsAndConditionsAction
	) {
		this.updatedTermsAndConditionsService.initialize();
	}

	ngOnInit() {
		if (this.termsAndConditionsHistoryEnabled) {
			this.store.dispatch(this.termsAndConditionsAction.getLatestVersion());
		}

		const jurisdiction$ = this.store.select(JurisdictionSelector.get).pipe(
			ignoreNil(),
			tap((jurisdiction) => (this.jurisdiction = jurisdiction))
		);

		const tabs$ = this.store.pipe(RouteSelector.getName$).pipe(
			tap((name: string) => {
				name === COMMON_ROUTE.termsAndConditions.root && this.jurisdiction === "Lotba"
					? this.navigateTo(this.TERMS_AND_CONDITIONS_ROUTES[0])
					: (this.selectedIndex = this.TERMS_AND_CONDITIONS_ROUTES.indexOf(name));
			})
		);

		const fetchHistory$ = this.store.select(AuthSelector.isAuthenticated).pipe(
			filter((isAuthenticated) => isAuthenticated),
			take(1),
			tap(() => {
				if (this.termsAndConditionsHistoryEnabled) {
					this.store.dispatch(this.termsAndConditionsAction.getHistory());
				}
			})
		);

		const isAcceptTermsAndConditionsFailed$ = this.acceptTermsAndConditionsFailed$().pipe(
			filter((x) => !!x?.length),
			take(1),
			tap((messages) => {
				this.errorMessage = messages[0].messageCode;
				this.windowRef.native.scrollTo(0, 0);
			})
		);

		const errorMessage$ = this.updatedTermsAndConditionsService.errorMessage$.pipe(
			tap((message) => {
				this.errorMessage = message;
			})
		);

		const hasDeclined$ = this.store.pipe(
			select(CustomerManagementSelector.hasDeclinedLatestTermsAndConditions),
			filter((isDeclined) => !!isDeclined),
			tap(() => {
				this.updatedTermsAndConditionsService.declinedTermsAndConditionsSnackbar();
				this.updatedTermsAndConditionsService.setDeclineWarningState(false);
			})
		);

		const history$ = this.store.select(this.termsAndConditionsSelector.getHistory()).pipe(
			ignoreNil(),
			tap((history: TermsAndConditionsVersion[]) => {
				history = history.filter((item) => item.isAccepted === true);
				this.versionHistory = history;
				this.currentVersion = this.versionHistory[0];
			})
		);

		this.data$$ = merge(
			isAcceptTermsAndConditionsFailed$,
			jurisdiction$,
			hasDeclined$,
			errorMessage$,
			history$,
			fetchHistory$,
			tabs$
		).subscribe(() => this.changeDetector.markForCheck());
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	selectTab(tabIndex: number) {
		const route = this.TERMS_AND_CONDITIONS_ROUTES[tabIndex];
		this.navigateTo(route);
	}

	private navigateTo(route: string) {
		this.store.dispatch(RouteAction.navigate(route, undefined, { replaceUrl: true }));
	}

	// TODO: remove the terms and conditions duplications when we move this from Angular
	private acceptTermsAndConditionsFailed$(): Observable<Message[]> {
		return this.messageService.consume$({
			context: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.termsAndConditions,
			messageType: MessageType.Error,
		});
	}
}
